/**
 * Interface for work log entries used in overlap detection.
 */
export interface TimeLogEntry {
  id: string;
  startedTime: string;
  timeSpentSeconds: number;
  issueKey?: string;
  issueId?: string;
}

/**
 * Detects overlapping work log entries.
 * Two entries overlap if: startA < endB AND startB < endA
 *
 * @param logs - Array of work log entries to check
 * @param getKey - Optional function to get display key for an entry
 * @returns Map where key is log ID and value is array of overlapping log keys
 */
export const detectOverlaps = (
  logs: TimeLogEntry[],
  getKey?: (log: TimeLogEntry) => string
): Map<string, string[]> => {
  const overlaps = new Map<string, string[]>();
  const keyFn = getKey || ((log: TimeLogEntry) => log.issueKey || log.issueId || log.id);

  for (let i = 0; i < logs.length; i++) {
    const logA = logs[i];
    const startA = new Date(logA.startedTime).getTime();
    const endA = startA + logA.timeSpentSeconds * 1000;

    for (let j = i + 1; j < logs.length; j++) {
      const logB = logs[j];
      const startB = new Date(logB.startedTime).getTime();
      const endB = startB + logB.timeSpentSeconds * 1000;

      // Check if intervals overlap
      if (startA < endB && startB < endA) {
        const keyA = keyFn(logA);
        const keyB = keyFn(logB);

        // Add overlap for log A
        const overlapsA = overlaps.get(logA.id) || [];
        overlapsA.push(keyB);
        overlaps.set(logA.id, overlapsA);

        // Add overlap for log B
        const overlapsB = overlaps.get(logB.id) || [];
        overlapsB.push(keyA);
        overlaps.set(logB.id, overlapsB);
      }
    }
  }

  return overlaps;
};
